import { useState, RefObject } from "react";
import html2pdf from "html2pdf.js";
import { toast } from "@/hooks/use-toast";

const prepareSVGsForPDF = (element: HTMLElement) => {
  const svgElements = element.querySelectorAll('svg');
  console.log(`[PDF] Preparando ${svgElements.length} SVGs para exportação`);
  
  svgElements.forEach((svg, index) => {
    const rect = svg.getBoundingClientRect();
    console.log(`[PDF] SVG ${index}: width=${rect.width}, height=${rect.height}`);
    
    // Se dimensões são zero, usar dimensões padrão
    const width = rect.width > 0 ? rect.width : 800;
    const height = rect.height > 0 ? rect.height : 400;
    
    svg.setAttribute('width', width.toString());
    svg.setAttribute('height', height.toString());
    svg.style.width = `${width}px`;
    svg.style.height = `${height}px`;
  });
};

export function useExportToPDF(elementRef: RefObject<HTMLElement>, defaultFilename = "export.pdf") {
  const [isExporting, setIsExporting] = useState(false);

  const exportToPDF = async (customFilename?: string) => {
    if (!elementRef.current) {
      toast({
        title: "Erro ao exportar",
        description: "Elemento não encontrado para exportação.",
        variant: "destructive",
      });
      return;
    }

    setIsExporting(true);

    const element = elementRef.current;
    let exportContainer: HTMLDivElement | null = null;

    try {
      const clonedElement = element.cloneNode(true) as HTMLElement;
      clonedElement.style.display = "block";
      clonedElement.style.visibility = "visible";
      clonedElement.style.opacity = "1";
      exportContainer = document.createElement("div");
      exportContainer.style.position = "fixed";
      exportContainer.style.left = "-10000px";
      exportContainer.style.top = "0";
      exportContainer.style.width = "794px";
      clonedElement.style.width = "794px";
      clonedElement.style.maxWidth = "794px";
      clonedElement.style.boxSizing = "border-box";
      clonedElement.style.overflow = "hidden";
      exportContainer.style.opacity = "1";
      exportContainer.style.visibility = "visible";
      exportContainer.style.pointerEvents = "none";
      exportContainer.style.background = "#ffffff";
      exportContainer.style.zIndex = "-1";
      exportContainer.appendChild(clonedElement);
      document.body.appendChild(exportContainer);

      // Garante que o clone foi pintado antes do html2canvas.
      await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
      await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

      prepareSVGsForPDF(clonedElement);

      const opt = {
        margin: 0,
        filename: customFilename || defaultFilename,
        image: { type: "jpeg" as const, quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          windowWidth: 794,
          windowHeight: 1123,
          width: 794,
        },
        jsPDF: {
          unit: "mm" as const,
          format: "a4" as const,
          orientation: "portrait" as const,
        },
        pagebreak: { mode: ["css"] as const },
      };

      await html2pdf().set(opt).from(clonedElement).save();

      toast({
        title: "PDF gerado com sucesso!",
        description: `Arquivo ${opt.filename} foi baixado.`,
      });
    } catch (error) {
      console.error("[PDF] Erro ao gerar PDF:", error);
      toast({
        title: "Erro ao gerar PDF",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      if (exportContainer?.parentNode) {
        exportContainer.parentNode.removeChild(exportContainer);
      }
      setIsExporting(false);
    }
  };

  return { exportToPDF, isExporting };
}
